import { themeVar } from '@/styles/theme';
import { Dispatch, SetStateAction } from 'react';
import styled, { css } from 'styled-components';

import { InfoIcon } from '@/assets/icons/info-icons/info';
import { Tooltip } from '@/components/basicComponents/tooltip';
import Switcher from '@/components/dealCatalog-page/switcher';
import { useResponsive } from '../../../../hooks/use-responsive';
import { formatByType } from './formatSettings';
import SectorsToAvoid from './sectorsToAvoid';
import { InfoListRow } from './types';

interface InfoListProps {
  data: InfoListRow[];
  isSwitcherActive?: boolean;
  setIsSwitcherActive?: Dispatch<SetStateAction<boolean>>;
  label?: string;
  isTwoColumn?: boolean;
}

export const InfoList = ({ isSwitcherActive, setIsSwitcherActive, label, data, isTwoColumn = false }: InfoListProps) => {
  const changeSwitcher = () => setIsSwitcherActive?.(!isSwitcherActive);
  const checkISArrayFilled = (array: string[]) => !!array.filter((item) => item).length;
  const { isMobile } = useResponsive();

  const cleanData = () =>
    data.filter(({ value }) => {
      if (Array.isArray(value)) return checkISArrayFilled(value);
      return value !== null && value !== undefined;
    });

  return (
    <MoreDataWrap>
      {isSwitcherActive !== undefined && (
        <WrapperSwitcher>
          <Switcher isActive={isSwitcherActive} onClick={changeSwitcher} />
          {label ?? ''}
        </WrapperSwitcher>
      )}
      <ElementsWrap isTwoColumn={!isMobile && isTwoColumn}>
        {cleanData().map(({ name, tooltipName, value, formatType }) => (
          <MoreDataItem isTwoColumn={!isMobile && isTwoColumn} key={name}>
            <MoreDataItemTitle isMobile={isMobile}>
              <p>{name}</p>
              {tooltipName && (
                <Tooltip title={tooltipName}>
                  <InfoIcon fill={'#ABABAB'} style={{ position: 'relative', top: '3px' }} />
                </Tooltip>
              )}
            </MoreDataItemTitle>
            {Array.isArray(value) ? (
              <SectorsToAvoid list={value} />
            ) : (
              <MoreDataItemValue isMobile={isMobile}>{formatByType[formatType](value as never)}</MoreDataItemValue>
            )}
          </MoreDataItem>
        ))}
      </ElementsWrap>
    </MoreDataWrap>
  );
};

const WrapperSwitcher = styled.div`
  display: flex;
  column-gap: 10px;
  margin-bottom: 22px;
  color: ${themeVar('font.base')};
`;

const MoreDataItemValue = styled.div<{ isMobile?: boolean }>`
  font-family: Blinker, serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
  letter-spacing: 0;
  text-align: left;
  width: ${({ isMobile }) => (isMobile ? '50%' : '180px')};
  color: ${themeVar('font.base')};
`;

const MoreDataItemTitle = styled.div<{ isMobile?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ isMobile }) => (isMobile ? '20px' : '8px')};
  font-family: Blinker, serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0;
  text-align: left;
  width: ${({ isMobile }) => (isMobile ? '50%' : '150px')};
  min-width: ${({ isMobile }) => (isMobile ? '50%' : '150px')};
  color: ${themeVar('font.base')};
`;

const MoreDataItem = styled.div<{ isTwoColumn: boolean }>`
  width: ${({ isTwoColumn }) => (isTwoColumn ? '50%' : '100%')};
  display: flex;
  align-items: center;
  padding: 5px 20px;
  background-color: ${themeVar('layer.1')};
  gap: 10px;
  border-radius: 4px;
`;

const ElementsWrap = styled.div<{ isTwoColumn: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 3px;
  ${({ isTwoColumn }) =>
    isTwoColumn
      ? css`
          flex-wrap: wrap;
          max-height: 350px;
        `
      : ''}
`;

const MoreDataWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;
